'use client';

import { Button } from '@common/components';
import { useToast } from '@common/hooks';
import { useMutation } from '@tanstack/react-query';
import { delay } from 'es-toolkit';

import { AuthApi } from '@/api';
import { ENV, ROUTES, SEARCH_PARAMS } from '@/constants';
import { useAccessToken } from '@/hooks/useAccessToken';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { KB_PAY_CUSTOM_SCHEME } from '@/modules/kb-pay';
import { ANDROID_DEEPLINK, CUSTOM_SCHEME } from '@/modules/wello-app';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { closeResponsiveDialog } from '@/stores/DrawerStore';

export const KosafHouseholdConsentContent = () => {
  const isKbWebview = useDeviceInfoStore(
    (state) => state.webviewType === WEBVIEW_TYPE.KB_PAY,
  );
  const { isWelloApp } = useCheckWelloApp();
  const { toast, toastError } = useToast();
  const { accessToken, refreshToken } = useAccessToken();
  const isIos = useDeviceInfoStore((state) => state.osType) === 'ios';

  const { mutate: requestKosafConfirmUrl, isPending } = useMutation({
    mutationFn: () => {
      //* 앱일 경우 OS별 앱 스킴, 웹은 NEXT_PUBLIC_DOMAIN 기준 (localhost는 서버 화이트리스트 미포함 → CORS)
      const fallbackUrl = `${ENV.NEXT_PUBLIC_DOMAIN}${ROUTES.MY.pathname}`;
      const returnUrl =
        isWelloApp || isKbWebview
          ? isIos
            ? CUSTOM_SCHEME.WELLO_APP_PREFIX
            : `${CUSTOM_SCHEME.ANDROID_APP_PREFIX}${ANDROID_DEEPLINK}`
          : fallbackUrl;

      return AuthApi[
        'get:/wello/v2/auth/kosaf/confirm-household-info-agreement'
      ]({
        query: { redirectUrl: returnUrl, cancelUrl: returnUrl },
      });
    },
    onSuccess: async (data) => {
      const responseUrl = data.data?.context?.responseUrl;

      if (!responseUrl) {
        toast({
          message: '요청에 필요한 정보를 가져오지 못했습니다.',
          type: 'fail',
        });

        return;
      }

      const confirmUrl = new URL(responseUrl);

      //* 환경별 분기: 웰로앱 / KB Pay 웹뷰는 cross-domain-callback으로 토큰 전달, 일반 웹은 직접 리다이렉트
      if (isWelloApp) {
        const searchParams = Object.fromEntries(
          confirmUrl.searchParams.entries(),
        );

        window.location.href = `${CUSTOM_SCHEME.IN_APP_EXTERNAL_PREFIX}${ENV.NEXT_PUBLIC_DOMAIN}${ROUTES.CROSS_DOMAIN_CALLBACK.withSearchParams(
          {
            searchParams: {
              accessToken: accessToken as string,
              refreshToken: refreshToken as string,
              [SEARCH_PARAMS.REDIRECT]: confirmUrl.toString(),
              [SEARCH_PARAMS.REDIRECT_SEARCH_PARAMS]: JSON.stringify({
                ...searchParams,
              }),
            },
          },
        )}`;

        await delay(1000);
        closeResponsiveDialog();
      } else if (isKbWebview) {
        const searchParams = Object.fromEntries(
          confirmUrl.searchParams.entries(),
        );

        window.location.href = `${KB_PAY_CUSTOM_SCHEME.EXTERNAL_PREFIX}${ENV.NEXT_PUBLIC_DOMAIN}${ROUTES.CROSS_DOMAIN_CALLBACK.withSearchParams(
          {
            searchParams: {
              accessToken: accessToken as string,
              refreshToken: refreshToken as string,
              [SEARCH_PARAMS.REDIRECT]: confirmUrl.toString(),
              [SEARCH_PARAMS.REDIRECT_SEARCH_PARAMS]: JSON.stringify({
                ...searchParams,
              }),
            },
          },
        )}`;

        await delay(1000);
        closeResponsiveDialog();
      } else {
        window.location.replace(confirmUrl.toString());
      }
    },
    onError: (error) => {
      toastError({ error });
    },
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 px-20 pt-16">
        <h1 className="mb-32 font-700 typo-h5">
          가구원동의 서비스 이용을 위한
          <br />
          제3자 제공 동의
        </h1>
        <p className="mb-8 font-700 text-Gray-800 typo-body3">
          [한국장학재단 가구원 정보 확인 서비스]
        </p>
        <ul className="pl-20 text-Gray-800 typo-body4">
          <li className="list-disc">
            개인정보를 제공받는 자: <strong>한국장학재단</strong>
          </li>
          <li className="list-disc">
            제공받는 자의 이용 목적:{' '}
            <strong>학자금 지원 구간 산정을 위한 가구원 정보 확인</strong>
          </li>
          <li className="list-disc">제공하는 개인정보 항목: CI(연계정보)</li>
          <li className="list-disc">
            제공받는 자의 보유 및 이용 기한:{' '}
            <strong>
              가구원 정보 확인 목적 달성 시까지 또는 관련 법령에 따른 보관 의무
              기간
            </strong>
          </li>
          <li className="list-disc">
            동의 거부 권리 및 불이익: 개인정보 제3자 제공에 동의하지 않을 경우
            &apos;한국장학재단 가구원 정보 확인&apos; 서비스 이용이 불가 합니다.
          </li>
        </ul>
      </div>

      <footer className="sticky bottom-0 bg-Gray-white px-20 py-16">
        <Button
          className="w-full"
          onClick={() => requestKosafConfirmUrl()}
          disabled={isPending}
          loading={isPending}
        >
          동의하고 신청하기
        </Button>
      </footer>
    </div>
  );
};
